import React, {
  useState,
  useEffect,
  useCallback,
} from "react";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";
import { HiArrowSmLeft } from "react-icons/hi";
import { format } from 'date-fns';
import { FaTag, FaCheckCircle } from "react-icons/fa";
import config from "../../config";

const FormField = React.memo(({ label, required, error, children, labelCheckBoxNode = "", classApplied = "" }) => (
  <div className={`space-y-2 ${classApplied}`}>
    <label className="block">
      {label} {required && <span className="text-red-500">*</span>}
      {labelCheckBoxNode}
    </label>
    {children}
    {error && <p className="text-red-500 text-sm">{error}</p>}
  </div>
));

FormField.displayName = "FormField";

const S3_BASE_URL = (config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL || "https://flps2025.s3.ap-south-1.amazonaws.com").replace(/\/+$/, "");
const resolveS3Url = (maybeRelativePath) => {
  if (!maybeRelativePath) return "";
  if (/^https?:\/\//i.test(maybeRelativePath)) return maybeRelativePath;
  const relative = String(maybeRelativePath).replace(/^\/+/, "");
  return `${S3_BASE_URL}/${relative}`;
};

const SupplierPaymentDrawer = ({
  isOpen,
  supplierId,
  purchaseBillId,
  onClose,
  onSave,
}) => {
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [purchaseBills, setPurchaseBills] = useState([]);
  const [selectedPurchaseBill, setSelectedPurchaseBill] = useState(null);
  const [amountType, setAmountType] = useState("full");
  const [supplierUPI, setSupplierUPI] = useState({ upiId: "", qrCodeUrl: "" });
  const [isFormValid, setIsFormValid] = useState(false);
  const [totalReturnAmount, setTotalReturnAmount] = useState(0);

  // ── Credit Note States ──────────────────────────────────────────────────────
  const [availableCredits, setAvailableCredits] = useState([]);   // all active/partially redeemed credits
  const [selectedCreditIds, setSelectedCreditIds] = useState([]); // checked credit IDs
  const [loadingCredits, setLoadingCredits] = useState(false);

  const [formData, setFormData] = useState({
    vendorPurchaseId: "",
    paidAmount: 0,
    paymentMode: "",
    paymentRef: "",
    status: "",
  });

  // ── Derived: total credit amount selected ──────────────────────────────────
  const totalCreditApplied = availableCredits
    .filter(c => selectedCreditIds.includes(c._id))
    .reduce((sum, c) => sum + Number(c.remaining_amount ?? c.credit_amount ?? 0), 0);

  // ── Due = invoice_due − credits_applied ────────────────────────────────────
  const rawDue = Math.max(
    0,
    Number(selectedPurchaseBill?.total_amount || 0) -
    Number(selectedPurchaseBill?.paid_amount || 0)
  );
  const dueAmount = Math.max(0, rawDue - totalCreditApplied);

  // ── Fetch available credits when supplier changes ──────────────────────────
  useEffect(() => {
    if (isOpen && supplierId) {
      fetchAvailableCredits();
    }
  }, [isOpen, supplierId]);

  const fetchAvailableCredits = async () => {
    setLoadingCredits(true);
    try {
      const response = await httpService.get(
        `/vendorsuppliercredit/getVendorSupplierCredits/${supplierId}?status=Active,Partial%20Redeemed`
      );
      if (response.status && response.code === 200) {
        setAvailableCredits(response.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch credits", err);
    } finally {
      setLoadingCredits(false);
    }
  };

  const toggleCredit = (creditId) => {
    setSelectedCreditIds(prev =>
      prev.includes(creditId)
        ? prev.filter(id => id !== creditId)
        : [...prev, creditId]
    );
  };

  const checkFormValidity = useCallback(() => {
    if (!selectedPurchaseBill) return false;
    const requiredFieldsValid = {
      vendorPurchaseId: !!formData.vendorPurchaseId,
      paidAmount: Number(formData.paidAmount) >= 0 && (Number(formData.paidAmount) > 0 || totalCreditApplied > 0),
      paymentMode: dueAmount === 0 ? true : formData.paymentMode?.trim() !== "",
    };
    if (formData.paymentMode !== "cash" && dueAmount > 0) {
      requiredFieldsValid.paymentRef = formData.paymentRef?.trim() !== "";
    }
    return Object.values(requiredFieldsValid).every(Boolean);
  }, [selectedPurchaseBill, formData, totalCreditApplied, dueAmount]);

  useEffect(() => {
    setIsFormValid(checkFormValidity());
  }, [formData, selectedPurchaseBill, checkFormValidity, totalCreditApplied]);

  // Auto-fill paidAmount when dueAmount changes
  useEffect(() => {
    if (isOpen && selectedPurchaseBill) {
      if (amountType === "full") {
        setFormData((prev) => ({ ...prev, paidAmount: dueAmount }));
        validate("paidAmount", dueAmount);
      } else {
        setFormData((prev) => ({ ...prev, paidAmount: 0 }));
        validate("paidAmount", 0);
      }
    }
  }, [isOpen, amountType, dueAmount]);

  const resetFormData = () => {
    setFormData({
      vendorPurchaseId: "",
      paidAmount: 0,
      paymentMode: "",
      paymentRef: "",
      status: "",
    });
  };

  const validate = useCallback(
    (field, value) => {
      const newErrors = { ...errors };
      switch (field) {
        case "paymentRef":
          if (!value?.trim()) newErrors.paymentRef = "Payment Reference is required";
          else delete newErrors.paymentRef;
          break;
        case "paymentMode":
          if (!value?.trim()) newErrors.paymentMode = "Payment Mode is required";
          else delete newErrors.paymentMode;
          break;
        case "paidAmount":
          if (!(value >= 0)) newErrors.paidAmount = "Amount is required";
          else delete newErrors.paidAmount;
          break;
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    [errors]
  );

  const handleBlur = useCallback(
    (field) => {
      setTouched((prev) => ({ ...prev, [field]: true }));
      validate(field, formData[field]);
    },
    [validate, formData]
  );

  const handleChange = useCallback(
    (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      validate(field, value);
    },
    [validate]
  );

  const resetAllStates = () => {
    resetFormData();
    setShowConfirmDialog(false);
    setIsSaving(false);
    setTouched({});
    setErrors({});
    setLoading(false);
    setPurchaseBills([]);
    setSelectedPurchaseBill(null);
    setAmountType("full");
    setIsFormValid(false);
    setTotalReturnAmount(0);
    setSupplierUPI({ upiId: "", qrCodeUrl: "" });
    setAvailableCredits([]);
    setSelectedCreditIds([]);
  };

  const handleClose = () => {
    resetAllStates();
    onClose();
  };

  const confirmClose = () => {
    setShowConfirmDialog(false);
    resetAllStates();
    onClose();
  };

  useEffect(() => {
    if (supplierId && isOpen) {
      fetchSupplierData();
    }
  }, [supplierId, isOpen]);

  const fetchSupplierData = async () => {
    setLoading(true);
    try {
      const endpoint = purchaseBillId
        ? `vendorpurchase/getVendorSupplierPurchases/${supplierId}`
        : `vendorpurchase/getVendorSupplierPurchases/${supplierId}?paymentStatus=unpaid`;

      const [billsRes, supplierRes] = await Promise.all([
        httpService.get(endpoint),
        httpService.get(`vendorsupplier/getVendorSupplier/${supplierId}`)
      ]);

      const data = billsRes.data || [];
      const totalReturn = Number(supplierRes?.data?.total_return_amount || 0);
      setTotalReturnAmount(totalReturn);

      let selectedBill = null;
      let upiId = "";
      let qrCodeUrl = "";

      if (supplierRes?.data?.upi_info && supplierRes.data.upi_info.length > 0) {
        const supplierUPIDetail = supplierRes.data.upi_info[0];
        upiId = supplierUPIDetail.upi_id || "";
        qrCodeUrl = resolveS3Url(supplierUPIDetail.qr_code || "");
      }

      if (purchaseBillId) {
        selectedBill = data.find((bill) => bill._id === purchaseBillId);
        if (selectedBill) {
          setPurchaseBills([selectedBill]);
          setSelectedPurchaseBill(selectedBill);
          setFormData((prev) => ({ ...prev, vendorPurchaseId: selectedBill._id }));
          if (selectedBill.supplier_upi_details && selectedBill.supplier_upi_details.length > 0) {
            const billUPIDetail = selectedBill.supplier_upi_details[0];
            upiId = billUPIDetail.upi_id || upiId;
            qrCodeUrl = resolveS3Url(billUPIDetail.qr_code || "") || qrCodeUrl;
          }
        } else {
          setPurchaseBills([]);
          setSelectedPurchaseBill(null);
        }
      } else {
        setPurchaseBills(data);
      }

      setSupplierUPI({ upiId, qrCodeUrl });
    } catch (err) {
      console.error("Failed to fetch supplier purchases", err);
      toast.error("Could not load supplier purchases");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    handleClose();
  };

  const handleSubmit = async () => {
    setIsSaving(true);

    const effectivePaid = Number(formData.paidAmount);

    if (effectivePaid < 0) {
      toast.error("Please enter a valid payment amount.");
      setIsSaving(false);
      return;
    }

    if (effectivePaid > dueAmount) {
      toast.error("Paid amount cannot be greater than the due amount.");
      setIsSaving(false);
      return;
    }

    if (!formData.vendorPurchaseId) {
      toast.error("Please select an invoice");
      setIsSaving(false);
      return;
    }

    if (effectivePaid === 0 && totalCreditApplied === 0) {
      toast.error("Either enter a payment amount or apply a credit note");
      setIsSaving(false);
      return;
    }

    if (dueAmount > 0 && !formData.paymentMode) {
      toast.error("Please select a payment mode");
      setIsSaving(false);
      return;
    }

    // ── Build credits_to_redeem ─────────────────────────────────────────────
    // For each selected credit, use its full remaining_amount
    // but cap it so we don't redeem more than what's actually owed
    let remainingToSettle = rawDue;
    const credits_to_redeem = availableCredits
      .filter(c => selectedCreditIds.includes(c._id))
      .map(c => {
        const available = Number(c.remaining_amount ?? c.credit_amount ?? 0);
        const amountToUse = Math.min(available, remainingToSettle);
        remainingToSettle -= amountToUse;
        return {
          credit_id: c._id,
          amount_to_use: amountToUse,
        };
      })
      .filter(entry => entry.amount_to_use > 0);
    const actualCreditApplied = credits_to_redeem.reduce(
      (sum, entry) => sum + Number(entry.amount_to_use || 0),
      0
    );

    const payload = {
      supplier_id:            supplierId,
      vendor_purchase_id:     formData.vendorPurchaseId,
      paid_amount:            effectivePaid,
      payment_mode:           dueAmount === 0 ? "credit" : formData.paymentMode,
      payment_reference:      formData.paymentRef,
      credits_to_redeem,                          // ✅ backend expects this
      credit_amount_applied:  actualCreditApplied, // exact credit amount being redeemed now
    };

    try {
      const response = await httpService.post(
        "vendorsupplierpayment/addVendorSupplierPayment",
        payload
      );

      const isSuccess =
        response?.status === true ||
        response?.success === true ||
        response?.data?.success === true ||
        response?.code === 200 ||
        response?.code === 201;

      if (isSuccess) {
        toast.success(response?.message || "Payment added successfully");
        if (onSave && typeof onSave === "function") {
          onSave(response.data || response);
        }
        onClose();
        resetAllStates();
      } else {
        const errorMsg = response?.message || response?.data?.message || "Failed to save payment";
        toast.error(errorMsg);
        setIsSaving(false);
      }
    } catch (err) {
      console.error("Save failed", err);
      const errorMsg = err.response?.data?.message || err.message || "Failed to save payment";
      toast.error(errorMsg);
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (selectedPurchaseBill) {
      const target = document.getElementById("paymentBlock");
      if (target) target.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedPurchaseBill]);

  const getDisabledReason = () => {
    if (!selectedPurchaseBill) return "Select an invoice first";
    if (!formData.vendorPurchaseId) return "Select an invoice first";
    if (Number(formData.paidAmount) === 0 && totalCreditApplied === 0) return "Enter an amount or apply a credit note";
    if (dueAmount > 0 && !formData.paymentMode) return "Select payment mode";
    if (dueAmount > 0 && formData.paymentMode !== "cash" && !formData.paymentRef?.trim()) return "Enter payment reference";
    return "";
  };

  const ValidationSummary = () => {
    const missingFields = [];
    if (!selectedPurchaseBill || !formData.vendorPurchaseId) missingFields.push("Invoice");
    if (Number(formData.paidAmount) === 0 && totalCreditApplied === 0) missingFields.push("Amount or Credit Note");
    if (dueAmount > 0 && !formData.paymentMode) missingFields.push("Payment Mode");
    if (dueAmount > 0 && formData.paymentMode && formData.paymentMode !== "cash" && !formData.paymentRef?.trim())
      missingFields.push("Payment Reference");
    if (missingFields.length === 0) return null;
    return (
      <div className="text-sm text-red-500 mt-2 p-2 bg-red-50 rounded border border-red-200">
        ⚠️ Please fill required fields: {missingFields.join(", ")}
      </div>
    );
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-50 transition-all duration-300 ease-in-out ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="absolute inset-0 bg-black bg-opacity-40 backdrop-blur-[2px]"
          onClick={handleClose}
        />
        <div
          className={`absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl transform transition-transform duration-300 ease-out ${
            isOpen ? "translate-x-0" : "translate-x-full"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Loading Skeleton */}
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-90 z-10">
              <div className="p-4 space-y-4 animate-pulse">
                <div className="h-8 bg-gray-200 rounded w-1/3"></div>
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                <div className="mt-8 space-y-6">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="space-y-3">
                      <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                      <div className="h-10 bg-gray-200 rounded"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex justify-start items-center p-2 border-b bg-white shadow-sm gap-2">
            <button
              onClick={handleClose}
              className="flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            >
              <HiArrowSmLeft className="w-8 h-8" />
            </button>
            <h2 className="text-2xl font-bold text-blue-900">Add Payment</h2>
          </div>

          {isOpen && (
            <div className="flex h-[calc(100vh-110px)]">
              <div className="w-full px-4 pb-4 mt-4 space-y-4 overflow-y-auto bg-white">

                {/* ── Invoice Selector ─────────────────────────────────────── */}
                <div className="mb-4">
                  <FormField label="Choose Invoice" required>
                    <select
                      value={formData.vendorPurchaseId}
                      onChange={(e) => {
                        const bill = purchaseBills.find((b) => b._id === e.target.value);
                        setSelectedPurchaseBill(bill);
                        setSelectedCreditIds([]); // reset credit selection on bill change
                        setFormData((prev) => ({
                          ...prev,
                          vendorPurchaseId: e.target.value,
                        }));
                      }}
                      className="block w-full p-2 border rounded"
                    >
                      <option value="">Select Invoice #</option>
                      {purchaseBills.map((bill) => (
                        <option key={bill._id} value={bill._id}>
                          {bill.invoice_number} (Dated:{" "}
                          {bill.invoice_date ? format(bill.invoice_date, "dd MMM yyyy") : "-"})
                        </option>
                      ))}
                    </select>
                  </FormField>

                  {selectedPurchaseBill && (
                    <div className="relative mt-4 mb-2">
                      <div className="pt-2">
                        <div className="flex flex-col gap-1 mb-2">
                          <div className="flex items-center justify-between text-gray-700 text-sm">
                            <span>
                              Invoice Amount:{" "}
                              <span className="font-semibold">₹{selectedPurchaseBill.amount}</span>
                            </span>
                            <span className={`font-semibold text-base ml-2 ${
                              selectedPurchaseBill.payment_status === "partial"
                                ? "text-yellow-600"
                                : selectedPurchaseBill.payment_status === "paid"
                                ? "text-green-600"
                                : "text-red-600"
                            }`}>
                              {selectedPurchaseBill.payment_status === "partial"
                                ? "Partial Paid"
                                : selectedPurchaseBill.payment_status === "paid"
                                ? "Paid"
                                : "Unpaid"}
                            </span>
                          </div>
                          <div className="text-gray-700 text-sm">
                            GST Amount:{" "}
                            <span className="font-semibold">₹{selectedPurchaseBill.total_gst}</span>
                          </div>
                          <div className="text-gray-700 text-sm">
                            Total Amount:{" "}
                            <span className="font-semibold">₹{selectedPurchaseBill.total_amount}</span>
                          </div>
                          <div className="text-gray-700 text-sm">
                            Paid Amount:{" "}
                            <span className="font-semibold">₹{selectedPurchaseBill.paid_amount}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Credit Notes Section ─────────────────────────────────── */}
                {selectedPurchaseBill && (
                  <div className="border rounded-lg overflow-hidden">
                    {/* Header row */}
                    <div className="flex items-center justify-between px-3 py-2 bg-blue-50 border-b">
                      <div className="flex items-center gap-2">
                        <FaTag className="text-blue-500 text-sm" />
                        <span className="text-sm font-semibold text-blue-800">Available Credit Notes</span>
                      </div>
                      {loadingCredits && (
                        <span className="text-xs text-gray-400 animate-pulse">Loading...</span>
                      )}
                      {!loadingCredits && availableCredits.length > 0 && (
                        <span className="text-xs text-blue-600 font-medium">
                          {selectedCreditIds.length}/{availableCredits.length} selected
                        </span>
                      )}
                    </div>

                    <div className="px-3 py-2">
                      {!loadingCredits && availableCredits.length === 0 && (
                        <p className="text-xs text-gray-400 py-1">No credit notes available for this supplier.</p>
                      )}

                      {!loadingCredits && availableCredits.length > 0 && (
                        <div className="space-y-2 mt-1">
                          {availableCredits.map((credit) => {
                            const isSelected = selectedCreditIds.includes(credit._id);
                            const creditAmt = Number(credit.remaining_amount ?? credit.credit_amount ?? 0);
                            return (
                              <label
                                key={credit._id}
                                className={`flex items-center justify-between gap-3 p-2 rounded-lg border cursor-pointer transition-all ${
                                  isSelected
                                    ? "bg-green-50 border-green-400"
                                    : "bg-white border-gray-200 hover:border-blue-300"
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleCredit(credit._id)}
                                    className="w-4 h-4 accent-green-600"
                                  />
                                  <div>
                                    <p className="text-xs font-medium text-gray-700">
                                      {credit.credit_reason || "Credit Note"}
                                    </p>
                                    <p className="text-xs text-gray-400">
                                      {credit.createdAt
                                        ? format(new Date(credit.createdAt), "dd MMM yyyy")
                                        : "—"}
                                      {credit.status === "Partial Redeemed" && (
                                        <span className="ml-2 text-yellow-600">(Partially Redeemed)</span>
                                      )}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <span className={`text-sm font-bold ${isSelected ? "text-green-600" : "text-gray-700"}`}>
                                    ₹{creditAmt.toFixed(2)}
                                  </span>
                                  {isSelected && (
                                    <FaCheckCircle className="text-green-500 text-xs ml-1 inline" />
                                  )}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}

                      {/* Credit summary */}
                      {totalCreditApplied > 0 && (
                        <div className="mt-2 flex items-center justify-between text-sm font-semibold text-green-700 bg-green-50 rounded px-2 py-1.5 border border-green-200">
                          <span>Credit Applied</span>
                          <span>− ₹{totalCreditApplied.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Due Amount (after credits) ───────────────────────────── */}
                {selectedPurchaseBill && (
                  <div>
                    <label className="block text-gray-700 text-sm mb-1 font-medium">
                      Due Amount {totalCreditApplied > 0 && <span className="text-green-600 text-xs">(after credit)</span>}
                    </label>
                    <input
                      type="text"
                      value={dueAmount.toFixed(2)}
                      readOnly
                      className="block w-full p-2 border border-gray-300 rounded text-gray-800 cursor-not-allowed bg-gray-50 font-semibold"
                    />
                    {dueAmount === 0 && totalCreditApplied > 0 && (
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <FaCheckCircle /> Full invoice amount covered by credit notes — no cash payment needed!
                      </p>
                    )}
                  </div>
                )}

                {/* ── Payment Fields (hidden when due = 0) ─────────────────── */}
                {selectedPurchaseBill && dueAmount > 0 && (
                  <div className="my-2 border-t" id="paymentBlock">
                    <div className="flex flex-col gap-6 mt-4">
                      <FormField label="Amount Type">
                        <div className="flex items-center space-x-6">
                          <label className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name="amountType"
                              value="full"
                              checked={amountType === "full"}
                              onChange={(e) => setAmountType(e.target.value)}
                            />
                            <span>Full</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name="amountType"
                              value="custom"
                              checked={amountType === "custom"}
                              onChange={(e) => setAmountType(e.target.value)}
                            />
                            <span>Partial</span>
                          </label>
                        </div>
                      </FormField>

                      <FormField
                        label="Amount"
                        required
                        error={touched.paidAmount && errors.paidAmount}
                      >
                        <input
                          type="number"
                          value={formData.paidAmount}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            if (val > dueAmount) {
                              toast.error("Amount entered exceeds due amount");
                              return;
                            }
                            handleChange("paidAmount", val);
                          }}
                          readOnly={amountType === "full"}
                          onBlur={() => handleBlur("paidAmount")}
                          className={`block w-full p-2 border rounded ${
                            touched.paidAmount && errors.paidAmount
                              ? "border-red-300"
                              : "border-gray-300"
                          } ${amountType === "full" ? "bg-gray-50" : ""}`}
                          placeholder="Enter Amount"
                        />
                      </FormField>

                      <FormField
                        label="Payment Mode"
                        required
                        error={touched.paymentMode && errors.paymentMode}
                      >
                        <select
                          value={formData.paymentMode || ""}
                          onChange={(e) => handleChange("paymentMode", e.target.value)}
                          onBlur={() => handleBlur("paymentMode")}
                          className={`block w-full p-2 border rounded ${
                            touched.paymentMode && errors.paymentMode
                              ? "border-red-300"
                              : "border-gray-300"
                          }`}
                        >
                          <option value="">Select Payment Mode</option>
                          <option value="cash">Cash</option>
                          <option value="cheque">Cheque</option>
                          <option value="upi">UPI</option>
                          <option value="bank">Bank Transfer</option>
                        </select>
                      </FormField>

                      {/* UPI QR Code */}
                      {formData.paymentMode === "upi" && supplierUPI.qrCodeUrl && (
                        <div className="mt-2 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 shadow-sm">
                          <div className="flex flex-col items-center text-center">
                            <div className="mb-3">
                              <div className="w-32 h-32 bg-white rounded-xl shadow-md flex items-center justify-center p-2 border border-blue-100">
                                <img
                                  src={supplierUPI.qrCodeUrl}
                                  alt="UPI QR Code"
                                  className="w-full h-full object-contain"
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                    e.target.parentElement.innerHTML =
                                      '<div class="text-red-500 text-sm">QR code not available</div>';
                                  }}
                                />
                              </div>
                            </div>
                            {supplierUPI.upiId && (
                              <div className="space-y-1">
                                <p className="text-xs text-gray-500">Scan to pay via UPI</p>
                                <p className="text-sm font-medium text-gray-700 bg-white px-3 py-1 rounded-full inline-block border border-gray-200">
                                  {supplierUPI.upiId}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {formData.paymentMode !== "cash" && formData.paymentMode !== "upi" && (
                        <FormField
                          label="Payment Reference"
                          required={formData.paymentMode !== "cash"}
                          error={touched.paymentRef && errors.paymentRef}
                        >
                          <input
                            type="text"
                            value={formData.paymentRef || ""}
                            onChange={(e) => handleChange("paymentRef", e.target.value)}
                            onBlur={() => handleBlur("paymentRef")}
                            className={`block w-full p-2 border rounded ${
                              touched.paymentRef && errors.paymentRef
                                ? "border-red-300"
                                : "border-gray-300"
                            }`}
                            placeholder="Enter Payment Reference"
                          />
                        </FormField>
                      )}

                      {formData.paymentMode === "upi" && (
                        <FormField label="Payment Reference" error={touched.paymentRef && errors.paymentRef}>
                          <input
                            type="text"
                            value={formData.paymentRef || ""}
                            onChange={(e) => handleChange("paymentRef", e.target.value)}
                            onBlur={() => handleBlur("paymentRef")}
                            className="block w-full p-2 border border-gray-300 rounded"
                            placeholder="Enter UTR/Reference Number (optional)"
                          />
                        </FormField>
                      )}
                    </div>
                  </div>
                )}

                {selectedPurchaseBill && <ValidationSummary />}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="absolute w-full bottom-0 p-2 bg-white border-t shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
            <div className="flex flex-wrap items-center justify-end gap-4">
              <button
                type="button"
                onClick={handleCancel}
                className="min-w-[120px] px-4 py-2.5 border border-yellow-600 text-yellow-600 rounded-md hover:bg-yellow-50 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!isFormValid || isSaving}
                className={`min-w-[120px] px-4 py-2.5 text-white rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  !isFormValid || isSaving
                    ? "bg-gray-400 cursor-not-allowed focus:ring-gray-500"
                    : "bg-green-600 hover:bg-green-700 focus:ring-green-500"
                }`}
                title={!isFormValid ? getDisabledReason() : ""}
              >
                {isSaving ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SupplierPaymentDrawer;